import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ApiService from "../services/ApiService";
import CartService from "../services/CartService";
import SafeLocalStorage from "../utils/SafeLocalStorage";
import BackgroundCheckFormCacheService from "../services/BackgroundCheckFormCacheService";
import RatingDisplay from "./RatingDisplay";
import { calculateDiscountedPrice, CRJMC_PRICE } from "@/constants";
import { usePromo } from "@/contexts/PromoContext";
import { FormData } from "@/services/FormStorageService";
import { v4 as uuidv4 } from "uuid";

interface ButtonCTAProps {
  showSubheader?: boolean;
  subheaderText?: string;
  className?: string;
  buttonText?: string;
  discountPercent?: number;
}

const ButtonCTA: React.FC<ButtonCTAProps> = ({
  showSubheader = true,
  subheaderText = "No hidden fees",
  className,
  buttonText,
  discountPercent: propDiscountPercent,
}) => {
  // Generate a unique ID for this instance of ButtonCTA
  const instanceId = useRef<string>(uuidv4());
  // Get discount from context or props
  // Always call usePromo hook to maintain consistent hook order
  let contextDiscountPercent = null;
  try {
    const promoContext = usePromo();
    contextDiscountPercent = promoContext?.discountPercent ?? null;
  } catch (error) {
    // Context not available, continue with default values
    if (process.env.NODE_ENV === "development") {
      console.debug("PromoContext not available, using only prop discount");
    }
  }

  // Determine the effective discount percent (prop takes precedence over context)
  const [currentDiscountPercent, setCurrentDiscountPercent] = useState<
    number | null
  >(
    propDiscountPercent !== undefined
      ? propDiscountPercent
      : contextDiscountPercent,
  );

  // Update discount percent when props or context changes
  useEffect(() => {
    const newDiscountPercent =
      propDiscountPercent !== undefined
        ? propDiscountPercent
        : contextDiscountPercent;

    setCurrentDiscountPercent(newDiscountPercent);
  }, [propDiscountPercent, contextDiscountPercent]);

  // Calculate the display price based on the current discount percent
  const [actualDisplayPrice, setActualDisplayPrice] = useState<number>(
    currentDiscountPercent
      ? calculateDiscountedPrice(CRJMC_PRICE, currentDiscountPercent)
      : CRJMC_PRICE,
  );

  // Update display price when discount percent changes
  useEffect(() => {
    setActualDisplayPrice(
      currentDiscountPercent
        ? calculateDiscountedPrice(CRJMC_PRICE, currentDiscountPercent)
        : CRJMC_PRICE,
    );
  }, [currentDiscountPercent]);

  // Generate button text based on display price, discount, and custom text
  const defaultButtonText = `Get CRJMC now - CAD$${CRJMC_PRICE}`;
  const [actualButtonText, setActualButtonText] = useState<string>(
    buttonText || defaultButtonText,
  );
  const [previousButtonText, setPreviousButtonText] = useState<string>(
    buttonText || defaultButtonText,
  );

  // For animation tracking
  const [isTextChanging, setIsTextChanging] = useState(false);

  // Update button text when relevant values change
  // Use a ref to store the timeout ID for debouncing
  const textUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Use a ref to store the latest text to prevent race conditions
  const latestTextRef = useRef<string | null>(null);

  // Use state instead of a variable to track text changes
  const [textChanged, setTextChanged] = useState(false);

  useEffect(() => {
    // Only run this effect on the client side to avoid hydration mismatches
    if (typeof window === "undefined") return;

    // Store the latest text in a ref to prevent race conditions
    latestTextRef.current =
      buttonText ||
      `Get CRJMC now - CAD$${actualDisplayPrice}${currentDiscountPercent ? ` (${currentDiscountPercent}% off)` : ""}`;

    // Clear any existing timeout to implement debouncing
    if (textUpdateTimeoutRef.current) {
      clearTimeout(textUpdateTimeoutRef.current);
    }

    // Set a new timeout to update the text after a delay
    textUpdateTimeoutRef.current = setTimeout(() => {
      // Use the latest text from the ref to ensure we're using the most up-to-date value
      const textToUse = latestTextRef.current;

      if (textToUse && textToUse !== actualButtonText) {
        setTextChanged(true);
        setPreviousButtonText(actualButtonText);
        setIsTextChanging(true);
        setActualButtonText(textToUse);
      }
    }, 300); // 300ms debounce delay

    // Cleanup function to clear the timeout when component unmounts or dependencies change
    return () => {
      if (textUpdateTimeoutRef.current) {
        clearTimeout(textUpdateTimeoutRef.current);
      }
    };
  }, [
    actualDisplayPrice,
    currentDiscountPercent,
    buttonText,
    actualButtonText,
  ]); // Added actualButtonText back to ensure we catch all changes

  // Button styling
  const defaultClassName =
    "w-full bg-orange-500 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-orange-600 transition-colors shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-orange-300 focus:ring-opacity-50";

  // Form state management
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const firstNameInputRef = useRef<HTMLInputElement>(null);
  const scrollPositionRef = useRef(0);

  // Initialize form data from storage or with defaults
  const defaultFormData: FormData = {
    productType: "BACKGROUND_CHECK",
    firstName: "",
    middleNames: "",
    lastName: "",
    email: "",
    phone: "",
    termsAgreed: true,
  };

  const [formData, setFormData] = useState<FormData>(() => {
    const storedData = BackgroundCheckFormCacheService.getFormData();

    // If no data is stored, use default form data
    if (!storedData) {
      return defaultFormData;
    }

    // Merge stored data with default form data to ensure all fields exist
    // This handles cases where stored data is from a previous version of the form
    return {
      ...defaultFormData,
      ...storedData,
    };
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    BackgroundCheckFormCacheService.saveFormData(formData);
  }, [formData]);

  // Function to remove #open hash from URL
  const removeOpenHashFromUrl = () => {
    if (typeof window === "undefined") return;

    // Check if the current hash is #open or #open-[instanceId]
    if (
      window.location.hash === "#open" ||
      window.location.hash === `#open-${instanceId.current}`
    ) {
      // Remove the hash by replacing the current state
      window.history.replaceState(
        null,
        document.title,
        window.location.pathname + window.location.search,
      );
    }
  };

  // Function to add #open hash to URL
  const addOpenHashToUrl = () => {
    if (typeof window === "undefined") return;

    // Only add the hash if it's not already present
    if (
      window.location.hash !== "#open" &&
      window.location.hash !== `#open-${instanceId.current}`
    ) {
      // Add the hash by replacing the current state
      window.history.replaceState(
        null,
        document.title,
        window.location.pathname + window.location.search + "#open",
      );
    }
  };

  // Modal management - handle escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isModalOpen) {
        setIsModalOpen(false);
        removeOpenHashFromUrl();
      }
    };

    if (isModalOpen) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isModalOpen]);

  // Modal management - handle body scroll lock
  useEffect(() => {
    // Use a static counter to track open modals across all instances
    if (typeof window !== "undefined") {
      // Initialize the counter if it doesn't exist
      if (typeof window.__openModalCount === "undefined") {
        window.__openModalCount = 0;
      }

      if (isModalOpen) {
        // Increment the counter when a modal opens
        window.__openModalCount++;

        // Only apply styles if this is the first modal to open
        if (window.__openModalCount === 1) {
          // Save current scroll position
          scrollPositionRef.current = window.scrollY;

          // Calculate scrollbar width to prevent layout shift
          const scrollbarWidth =
            window.innerWidth - document.documentElement.clientWidth;

          // Check if device is mobile
          const isMobile = /iPhone|iPad|iPod|Android/i.test(
            navigator.userAgent,
          );

          if (!isMobile) {
            // Lock body scroll only on desktop
            document.body.style.position = "fixed";
            document.body.style.top = `-${scrollPositionRef.current}px`;
            document.body.style.left = "0";
            document.body.style.right = "0";
            document.body.style.width = "100%";
            document.body.style.paddingRight = `${scrollbarWidth}px`;
          }

          // Apply overflow hidden to both body and html to ensure no scrolling
          document.body.style.overflow = "hidden";
          document.documentElement.style.overflow = "hidden";
        }
      } else {
        // Decrement the counter when a modal closes
        if (window.__openModalCount > 0) {
          window.__openModalCount--;
        }

        // Only remove styles if all modals are closed
        if (window.__openModalCount === 0) {
          // If modal is closed, clean up any styles
          if (scrollPositionRef.current) {
            // Restore scroll position if we have one saved
            window.scrollTo(0, scrollPositionRef.current);
            scrollPositionRef.current = 0;
          }

          // Reset all styles
          document.body.style.position = "";
          document.body.style.top = "";
          document.body.style.left = "";
          document.body.style.right = "";
          document.body.style.width = "";
          document.body.style.paddingRight = "";
          document.body.style.overflow = "";
          document.documentElement.style.overflow = "";
        }
      }
    }

    // Cleanup function
    return () => {
      if (typeof window !== "undefined" && isModalOpen) {
        // Decrement the counter when component unmounts with modal open
        if (
          typeof window.__openModalCount !== "undefined" &&
          window.__openModalCount > 0
        ) {
          window.__openModalCount--;
        }

        // Only remove styles if all modals are closed
        if (window.__openModalCount === 0) {
          // Reset all styles
          document.body.style.position = "";
          document.body.style.top = "";
          document.body.style.left = "";
          document.body.style.right = "";
          document.body.style.width = "";
          document.body.style.paddingRight = "";
          document.body.style.overflow = "";
          document.documentElement.style.overflow = "";
        }
      }
    };
  }, [isModalOpen]);

  // Modal management - focus first input when modal opens
  useEffect(() => {
    if (isModalOpen && firstNameInputRef.current) {
      // Small delay to ensure the modal is fully rendered
      setTimeout(() => {
        firstNameInputRef.current?.focus();
      }, 50);
    }
  }, [isModalOpen]);

  // Modal management - handle browser back button
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handlePopState = () => {
      if (isModalOpen) {
        setIsModalOpen(false);
        removeOpenHashFromUrl();
      }
    };

    if (isModalOpen) {
      // Push state to history when modal opens, including the instance ID
      window.history.pushState(
        { modal: true, instanceId: instanceId.current },
        "",
      );
      window.addEventListener("popstate", handlePopState);
    }

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isModalOpen]);

  // Function to check if modal=open is in the URL query parameters
  const hasModalOpenQueryParam = (): boolean => {
    if (typeof window === "undefined") return false;

    // Parse the URL search parameters
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get("modal") === "open";
  };

  // Check for #open hash tag or modal=open query param in URL on component mount
  // or restore modal state from previous session
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Initialize the page load modal flag if it doesn't exist
    if (typeof window.__modalOpenedOnPageLoad === "undefined") {
      window.__modalOpenedOnPageLoad = false;
    }

    // Check if the URL hash is #open (general) or #open-[instanceId] (specific)
    // or if the URL has modal=open query parameter
    if (
      (window.location.hash === "#open" ||
        window.location.hash === `#open-${instanceId.current}` ||
        hasModalOpenQueryParam()) &&
      !window.__modalOpenedOnPageLoad
    ) {
      // Set the flag to true to prevent other modals from opening
      window.__modalOpenedOnPageLoad = true;
      setIsModalOpen(true);
      return;
    }

    // Create a unique key for this instance
    const modalStateKey = `modalWasOpen-${instanceId.current}`;

    // Check if modal was previously open before navigation
    const wasModalOpen = SafeLocalStorage.getItem(modalStateKey) === "true";
    if (wasModalOpen && !window.__modalOpenedOnPageLoad) {
      // Set the flag to true to prevent other modals from opening
      window.__modalOpenedOnPageLoad = true;
      // Clear the flag after restoring state
      SafeLocalStorage.removeItem(modalStateKey);
      setIsModalOpen(true);
    }
  }, []);

  // Save modal state when navigating away
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Create a unique key for this instance
    const modalStateKey = `modalWasOpen-${instanceId.current}`;

    // Function to save modal state before unload
    const saveModalState = () => {
      if (isModalOpen) {
        SafeLocalStorage.setItem(modalStateKey, "true");
      } else {
        SafeLocalStorage.removeItem(modalStateKey);
      }
    };

    // Add event listener for page navigation
    window.addEventListener("beforeunload", saveModalState);

    // Cleanup
    return () => {
      window.removeEventListener("beforeunload", saveModalState);
    };
  }, [isModalOpen]);

  // Form validation
  const validateForm = () => {
    const newErrors: Partial<FormData> = {};

    // First name validation
    if (
      !formData.firstName ||
      formData.firstName.length < 2 ||
      formData.firstName.length > 64
    ) {
      newErrors.firstName = "First name must be between 2 and 64 characters";
    }

    // Middle names validation (optional)
    if (formData.middleNames && formData.middleNames.length > 64) {
      newErrors.middleNames = "Middle names must not exceed 64 characters";
    }

    // Last name validation
    if (
      !formData.lastName ||
      formData.lastName.length < 1 ||
      formData.lastName.length > 64
    ) {
      newErrors.lastName = "Last name must be between 1 and 64 characters";
    }

    // Email validation
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Phone validation (optional)
    if (formData.phone && formData.phone.length > 64) {
      newErrors.phone = "Phone number must not exceed 64 characters";
    }

    // Terms of Service validation
    if (!formData.termsAgreed) {
      newErrors.termsAgreed = "You must agree to the Terms of Service";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submission handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Track event with Facebook Pixel if available
    if (typeof window !== "undefined" && window.fbq) {
      window.fbq("track", "InitiateCheckout");
    }

    setIsLoading(true);

    try {
      const response = await ApiService.submitBackgroundCheckForm(formData);

      // Save cart transaction state
      CartService.setCartTransaction({
        id: response.id,
        transactionId: response.transactionId,
        checkoutUrl: response.checkoutUrl,
      });

      // Save modal state before redirecting
      if (isModalOpen) {
        const modalStateKey = `modalWasOpen-${instanceId.current}`;
        SafeLocalStorage.setItem(modalStateKey, "true");
      }

      // Redirect to checkout URL
      window.location.href = response.checkoutUrl;

      // Reset state (though this won't execute due to the redirect)
      setIsModalOpen(false);
      removeOpenHashFromUrl();
    } catch (error) {
      console.error("Payment error:", error);
      // Here you might want to show an error message to the user
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Main CTA Button */}
      <div>
        <button
          onClick={() => {
            // Track view content event with Facebook Pixel if available
            if (typeof window !== "undefined" && window.fbq) {
              window.fbq("track", "ViewContent");
            }
            setIsModalOpen(true);
            addOpenHashToUrl();
          }}
          className={`${className || defaultClassName} relative overflow-hidden`}
        >
          <div
            className={`transition-all duration-300 absolute inset-0 flex items-center justify-center ${
              isTextChanging || textChanged
                ? "transform -translate-y-full opacity-0"
                : "transform translate-y-0 opacity-100"
            }`}
            onTransitionEnd={() => {
              if (isTextChanging) {
                // Set to false when transition ends to complete the animation cycle
                setIsTextChanging(false);
              }
            }}
          >
            {previousButtonText}
          </div>

          <div
            className={`transition-all duration-300 flex items-center justify-center ${
              isTextChanging || textChanged
                ? "transform translate-y-0 opacity-100"
                : "transform translate-y-full opacity-0"
            }`}
          >
            {actualButtonText}
          </div>
        </button>

        {/* Optional subheader text */}
        {showSubheader && (
          <div className="text-center mt-2 text-gray-300 text-sm">
            {subheaderText}
          </div>
        )}
      </div>

      {/* Modal Overlay - Using Portal to attach to body for better accessibility */}
      {isModalOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[100]"
            aria-modal="true"
            role="dialog"
          >
            {/* Semi-transparent backdrop - closes modal when clicked */}
            <div
              className="fixed inset-0 bg-black/60"
              onClick={() => {
                setIsModalOpen(false);
                removeOpenHashFromUrl();
              }}
              aria-hidden="true"
            />

            {/* Modal Content Container - Scrollable */}
            <div className="fixed inset-0 overflow-y-auto flex items-center justify-center">
              {/* Centering Container - Also acts as click target to close modal */}
              <div
                className="flex items-start sm:items-center justify-center p-0 sm:p-4 w-full max-sm:min-h-[100dvh]"
                onClick={(e) => {
                  // Close modal when clicking the flex container (outside modal)
                  if (e.target === e.currentTarget) {
                    setIsModalOpen(false);
                    removeOpenHashFromUrl();
                  }
                }}
              >
                {/* Modal Card Container */}
                <div
                  className="relative z-[101] w-full h-full sm:h-full sm:max-w-xl"
                  onClick={(e) => e.stopPropagation()} // Prevent clicks inside modal from closing it
                >
                  {/* Modal Card with responsive styling */}
                  <div className="bg-white dark:bg-gray-900 w-full rounded-none sm:rounded-lg max-md:min-h-[100dvh] max-md:max-h-[100dvh] flex flex-col shadow-xl">
                    {/* Header with Title and Close Button */}
                    <div className="flex-none flex items-center justify-between p-3 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                      <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                        Get your CRJMC in 15 minutes
                      </h2>
                      {/* Close button with accessibility attributes */}
                      <button
                        onClick={() => {
                          setIsModalOpen(false);
                          removeOpenHashFromUrl();
                        }}
                        className="ml-4 p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        aria-label="Close form"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* Form Container - Scrollable area with improved mobile handling */}
                    <div className="flex-1 overflow-y-auto -webkit-overflow-scrolling-touch">
                      <div className="p-3 sm:p-6">
                        {/* Form with validation and submission handling */}
                        <form
                          onSubmit={handleSubmit}
                          className="space-y-4"
                          style={{
                            touchAction: "manipulation",
                            /* Improve mobile input behavior */
                            WebkitTapHighlightColor: "transparent",
                          }}
                        >
                          {/* Form Fields */}
                          {/* First Name Field - Required (2-64 chars) */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 text-left">
                              First Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              ref={firstNameInputRef}
                              type="text"
                              value={formData.firstName}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  firstName: e.target.value,
                                })
                              }
                              onFocus={(e) => {
                                // Prevent layout shifts from causing focus to jump
                                setTimeout(() => {
                                  if (document.activeElement !== e.target) {
                                    e.target.focus();
                                  }
                                }, 100);
                              }}
                              className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-4 focus:ring-orange-300 focus:ring-opacity-50 focus:border-orange-500 focus:outline-none bg-white text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-white selection:bg-orange-200 selection:text-orange-900 dark:selection:bg-orange-500 dark:selection:text-white"
                              required
                              minLength={2}
                              maxLength={64}
                            />
                            {errors.firstName && (
                              <p className="mt-1 text-sm text-red-500">
                                {errors.firstName}
                              </p>
                            )}
                          </div>

                          {/* Middle Names Field - Optional (max 64 chars) */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 text-left">
                              Middle Names
                            </label>
                            <input
                              type="text"
                              value={formData.middleNames}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  middleNames: e.target.value,
                                })
                              }
                              onFocus={(e) => {
                                // Prevent layout shifts from causing focus to jump
                                setTimeout(() => {
                                  if (document.activeElement !== e.target) {
                                    e.target.focus();
                                  }
                                }, 100);
                              }}
                              className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-4 focus:ring-orange-300 focus:ring-opacity-50 focus:border-orange-500 focus:outline-none bg-white text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-white selection:bg-orange-200 selection:text-orange-900 dark:selection:bg-orange-500 dark:selection:text-white"
                              maxLength={64}
                            />
                            {errors.middleNames && (
                              <p className="mt-1 text-sm text-red-500">
                                {errors.middleNames}
                              </p>
                            )}
                          </div>

                          {/* Last Name Field - Required (1-64 chars) */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 text-left">
                              Last Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.lastName}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  lastName: e.target.value,
                                })
                              }
                              onFocus={(e) => {
                                // Prevent layout shifts from causing focus to jump
                                setTimeout(() => {
                                  if (document.activeElement !== e.target) {
                                    e.target.focus();
                                  }
                                }, 100);
                              }}
                              className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-4 focus:ring-orange-300 focus:ring-opacity-50 focus:border-orange-500 focus:outline-none bg-white text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-white selection:bg-orange-200 selection:text-orange-900 dark:selection:bg-orange-500 dark:selection:text-white"
                              required
                              maxLength={64}
                            />
                            {errors.lastName && (
                              <p className="mt-1 text-sm text-red-500">
                                {errors.lastName}
                              </p>
                            )}
                          </div>

                          {/* Email Field - Required (valid email format) */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 text-left">
                              Email <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              value={formData.email}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  email: e.target.value,
                                })
                              }
                              onFocus={(e) => {
                                // Prevent layout shifts from causing focus to jump
                                // Small delay to ensure the focus is maintained after any layout shifts
                                setTimeout(() => {
                                  if (document.activeElement !== e.target) {
                                    e.target.focus();
                                  }
                                }, 100);
                              }}
                              className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-4 focus:ring-orange-300 focus:ring-opacity-50 focus:border-orange-500 focus:outline-none bg-white text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-white selection:bg-orange-200 selection:text-orange-900 dark:selection:bg-orange-500 dark:selection:text-white"
                              required
                            />
                            {errors.email && (
                              <p className="mt-1 text-sm text-red-500">
                                {errors.email}
                              </p>
                            )}
                          </div>

                          {/* Phone Field - Optional (max 64 chars) */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 text-left">
                              Phone
                            </label>
                            <input
                              type="tel"
                              value={formData.phone}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  phone: e.target.value,
                                })
                              }
                              onFocus={(e) => {
                                // Prevent layout shifts from causing focus to jump
                                setTimeout(() => {
                                  if (document.activeElement !== e.target) {
                                    e.target.focus();
                                  }
                                }, 100);
                              }}
                              className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-4 focus:ring-orange-300 focus:ring-opacity-50 focus:border-orange-500 focus:outline-none bg-white text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-white selection:bg-orange-200 selection:text-orange-900 dark:selection:bg-orange-500 dark:selection:text-white"
                              maxLength={64}
                              placeholder="+12029182132"
                            />
                            {errors.phone && (
                              <p className="mt-1 text-sm text-red-500">
                                {errors.phone}
                              </p>
                            )}
                          </div>

                          {/* Terms of Service Checkbox - Required */}
                          <div
                            id="checkbox-terms-of-service"
                            className="flex items-start"
                          >
                            <div className="flex items-center h-5">
                              <input
                                id="terms"
                                type="checkbox"
                                checked={!!formData.termsAgreed}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    termsAgreed: e.target.checked,
                                  })
                                }
                                className="w-4 h-4 border border-gray-300 rounded bg-white focus:ring-3 focus:ring-orange-300 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-orange-600"
                                required
                              />
                            </div>
                            <div className="ml-3 text-sm">
                              <label
                                htmlFor="terms"
                                className="font-medium text-gray-700 dark:text-gray-300"
                              >
                                I agree to the{" "}
                                <a
                                  href="/terms-of-service/"
                                  target="_blank"
                                  className="text-orange-500 hover:underline"
                                >
                                  Terms of Service
                                </a>{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              {errors.termsAgreed && (
                                <p className="mt-1 text-sm text-red-500">
                                  {errors.termsAgreed}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Submit Button - Shows loading state and price */}
                          <button
                            type="submit"
                            disabled={isLoading}
                            className={`w-full bg-orange-500 text-white px-6 py-3 rounded-lg font-bold text-lg hover:bg-orange-600 transition-colors shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-orange-300 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed ${
                              isLoading ? "cursor-wait" : ""
                            }`}
                          >
                            {isLoading ? (
                              <div className="flex items-center justify-center">
                                {/* Loading spinner animation */}
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                Processing...
                              </div>
                            ) : (
                              `Order CRJMC for CA$${actualDisplayPrice}${currentDiscountPercent ? ` (${currentDiscountPercent}% off)` : ""}`
                            )}
                          </button>

                          {/* Trust Indicators - Ratings and reviews */}
                          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <RatingDisplay />
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default ButtonCTA;
